/**
 * Route-level RBAC (role-based access control) for Atlas.
 *
 * Defines which roles can access which routes. This is UI-level gating only
 * (Atlas is an internal engineering tool, not a multi-tenant system).
 */
import type { Route } from './hashRouter.js';

export type AtlasRole = 'engineer' | 'designer' | 'admin';

/** Map of route kind → required roles. Routes not listed are accessible to any authenticated user. */
export const ROUTE_ROLES: Partial<Record<Route['kind'], AtlasRole[]>> = {
  'compliance-hq': ['admin'],
  'pii-tracer': ['admin'],
  'lineage': ['admin'],
  'lint': ['engineer', 'admin'],
  'testdata': ['engineer', 'admin'],
  'simulate': ['engineer', 'admin'],
  'diff': ['engineer', 'admin'],
  'drift': ['engineer', 'admin'],
};

/**
 * Check if a user with the given role can access the route.
 *
 * @param routeId The route kind to check access for
 * @param userRole The user's role, or undefined if not authenticated
 * @returns true if the user can access the route
 *
 * Rules:
 * 1. Unauthenticated (userRole === undefined) → deny all routes
 * 2. Route not in ROUTE_ROLES → allow any authenticated user
 * 3. Route in ROUTE_ROLES → allow only if userRole is in the allowed list
 */
export function canAccessRoute(routeId: Route['kind'], userRole: AtlasRole | undefined): boolean {
  // Unauthenticated users cannot access any route
  if (userRole === undefined) {
    return false;
  }

  // Check if this route has restrictions
  const requiredRoles = ROUTE_ROLES[routeId];

  // No restriction → allow this authenticated user
  if (!requiredRoles) {
    return true;
  }

  // Has restriction → check if user's role is in the allowed list
  return requiredRoles.includes(userRole);
}
